import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
     pending: 'bg-orange-500/20 text-orange-300',
     confirmed: 'bg-green-500/20 text-green-300',
     completed: 'bg-blue-500/20 text-blue-300',
     cancelled: 'bg-red-500/20 text-red-300',
};

export default function CustomerDetail() {
     const { id } = useParams();
     const navigate = useNavigate();
     const [customer, setCustomer] = useState(null);
     const [bookings, setBookings] = useState([]);
     const [notes, setNotes] = useState('');
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          fetchData();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [id]);

     async function fetchData() {
          const [customerRes, bookingsRes] = await Promise.all([
               supabase.from('customers').select('*').eq('id', id).single(),
               supabase
                    .from('bookings')
                    .select('*')
                    .eq('customer_id', id)
                    .order('created_at', { ascending: false }),
          ]);

          setCustomer(customerRes.data);
          setNotes(customerRes.data?.notes || '');
          setBookings(bookingsRes.data || []);
          setLoading(false);
     }

     async function saveNotes() {
          await supabase
               .from('customers')
               .update({ notes, updated_at: new Date().toISOString() })
               .eq('id', id);
     }

     if (loading) return <p className='text-darker/60'>Loading customer...</p>;
     if (!customer) return <p className='text-darker/60'>Customer not found.</p>;

     const totalRevenue = bookings
          .filter((b) => b.status === 'confirmed' || b.status === 'completed')
          .reduce((sum, b) => sum + (b.total_cents || 0), 0);

     return (
          <div>
               <button
                    onClick={() => navigate('/admin/customers')}
                    className='text-darker/40 text-sm hover:text-darker duration-300 mb-4'
               >
                    ← Back to Customers
               </button>

               <h1 className='text-darker text-2xl font-bold mb-6'>{customer.name}</h1>

               <div className='grid grid-cols-1 tablet:grid-cols-3 gap-4 mb-6'>
                    <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                         <p className='text-darker text-2xl font-bold'>{bookings.length}</p>
                         <p className='text-darker/40 text-sm'>Total Bookings</p>
                    </div>
                    <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                         <p className='text-yellow text-2xl font-bold'>
                              ${(totalRevenue / 100).toLocaleString()}
                         </p>
                         <p className='text-darker/40 text-sm'>Total Revenue</p>
                    </div>
                    <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                         <p className='text-darker text-2xl font-bold'>
                              {format(new Date(customer.created_at), 'MMM yyyy')}
                         </p>
                         <p className='text-darker/40 text-sm'>Customer Since</p>
                    </div>
               </div>

               <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6 mb-6'>
                    {/* Contact Info */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>
                              Contact Information
                         </h3>
                         <div className='space-y-3 text-darker'>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Name</span>
                                   <span>{customer.name}</span>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Email</span>
                                   <a href={`mailto:${customer.email}`} className='text-yellow hover:underline'>
                                        {customer.email || '-'}
                                   </a>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Phone</span>
                                   <a href={`tel:${customer.phone}`} className='text-yellow hover:underline'>
                                        {customer.phone || '-'}
                                   </a>
                              </div>
                         </div>
                    </div>

                    {/* Notes */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>
                              Notes
                         </h3>
                         <textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className='w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300'
                              rows={4}
                              placeholder='Add notes about this customer...'
                         />
                         <button
                              onClick={saveNotes}
                              className='mt-2 text-sm text-yellow hover:underline'
                         >
                              Save Notes
                         </button>
                    </div>
               </div>

               {/* Booking History */}
               <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                    <div className='p-4 border-b border-gray-200'>
                         <h3 className='text-darker text-lg'>Booking History</h3>
                    </div>
                    {bookings.length === 0 ? (
                         <p className='text-darker/40 p-4'>No bookings yet.</p>
                    ) : (
                         <div className='overflow-x-auto'>
                              <table className='w-full text-left'>
                                   <thead>
                                        <tr className='border-b border-gray-200 text-darker/40 text-sm'>
                                             <th className='p-4'>Dates</th>
                                             <th className='p-4'>Amount</th>
                                             <th className='p-4'>Status</th>
                                             <th className='p-4'>Equipment</th>
                                             <th className='p-4'></th>
                                        </tr>
                                   </thead>
                                   <tbody className='divide-y divide-gray-200'>
                                        {bookings.map((b) => (
                                             <tr key={b.id} className='hover:bg-gray-100 duration-300'>
                                                  <td className='p-4 text-darker text-sm whitespace-nowrap'>
                                                       {format(new Date(b.start_date), 'MMM d')} –{' '}
                                                       {format(new Date(b.end_date), 'MMM d, yyyy')}
                                                  </td>
                                                  <td className='p-4 text-yellow font-bold text-sm'>
                                                       ${(b.total_cents / 100).toLocaleString()}
                                                  </td>
                                                  <td className='p-4'>
                                                       <span className={`px-2 py-1 rounded-md text-xs capitalize ${STATUS_COLORS[b.status] || ''}`}>
                                                            {b.status}
                                                       </span>
                                                  </td>
                                                  <td className='p-4 text-darker/40 text-sm'>
                                                       {b.checkout_status === 'checked_out' && (
                                                            <span className='px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-300'>Out</span>
                                                       )}
                                                       {b.checkout_status === 'checked_in' && (
                                                            <span className='px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-300'>Returned</span>
                                                       )}
                                                  </td>
                                                  <td className='p-4'>
                                                       <Link
                                                            to={`/admin/bookings/${b.id}`}
                                                            className='text-yellow text-sm hover:underline'
                                                       >
                                                            View →
                                                       </Link>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         </div>
                    )}
               </div>
          </div>
     );
}
